/**
 * Precision constants for different asset types and cryptocurrencies
 * Based on industry standards (Binance, Coinbase, Kraken)
 */

import { AssetType } from '../../types/formatting';
import {  AssetPrecisionConfig } from '../formatting';

/**
 * Default decimal places for different asset types
 * These determine how many decimals are shown by default
 */
export const DEFAULT_ASSET_DECIMALS: Record<AssetType, number> = {
  [AssetType.CRYPTO]: 8, // Bitcoin-standard precision
  // [AssetType.MEME_COIN]: 6, // PEPE, SHIB typically need 6-8
  [AssetType.STOCK]: 2, // US stocks use 2 decimals
  [AssetType.USD]: 2, // USD currency
};

/**
 * Calculation precision (internal accuracy)
 * These are NEVER shown, only used for calculations
 */
export const CALCULATION_DECIMALS: Record<AssetType, number> = {
  [AssetType.CRYPTO]: 18, // Maximum precision for crypto
  // [AssetType.MEME_COIN]: 18, // Meme coins can have many decimals
  [AssetType.STOCK]: 8, // High precision for fractional shares
  [AssetType.USD]: 8, // 8 decimals for cent fractions
};

/**
 * Comprehensive precision configs for different assets
 */
export const ASSET_PRECISION_CONFIGS: Record<AssetType, AssetPrecisionConfig> = {
  [AssetType.CRYPTO]: {
    assetType: AssetType.CRYPTO,
    displayDecimals: 8,
    calculationDecimals: 18,
    isFractional: true,
    minDisplay: '0.00000001', // 1 Satoshi
    symbolPatterns: ['BTC', 'ETH', 'XRP', 'LTC'],
  },
  // [AssetType.MEME_COIN]: {
  //   assetType: AssetType.MEME_COIN,
  //   displayDecimals: 6,
  //   calculationDecimals: 18,
  //   isFractional: true,
  //   minDisplay: '0.000001',
  //   symbolPatterns: ['PEPE', 'SHIB', 'DOGE', 'FLOKI'],
  // },
  [AssetType.STOCK]: {
    assetType: AssetType.STOCK,
    displayDecimals: 2,
    calculationDecimals: 8,
    isFractional: true, // Fractional shares
    minDisplay: '0.01',
    symbolPatterns: ['AAPL', 'GOOGL', 'MSFT', 'TSLA'],
  },
  [AssetType.USD]: {
    assetType: AssetType.USD,
    displayDecimals: 2,
    calculationDecimals: 8,
    isFractional: false,
    minDisplay: '0.01',
  },
};

// /**
//  * Meme coin configuration - additional symbols to watch
//  */
// export const MEME_COIN_SYMBOLS = new Set([
//   'PEPE',
//   'SHIB',
//   'DOGE',
//   'FLOKI',
//   'KISHU',
//   'ELON',
//   'DOGECOIN',
//   'SHIBA',
//   'PEPECOIN',
// ]);

// /**
//  * Common cryptocurrencies with their specific decimals
//  */
// export const CRYPTO_SYMBOL_DECIMALS: Record<string, number> = {
//   // Major coins
//   BTC: 8,
//   ETH: 18,
//   XRP: 6,
//   LTC: 8,
//   BCH: 8,
//   ETC: 18,
//   XMR: 12,
//   ZEC: 8,
//   DASH: 8,
//   // Stablecoins
//   USDT: 6,
//   USDC: 6,
//   BUSD: 18,
//   DAI: 18,
//   TUSD: 18,
//   // Meme coins (very small decimals)
//   PEPE: 18,
//   SHIB: 18,
//   DOGE: 8,
//   FLOKI: 9,
//   // Popular tokens
//   ADA: 6,
//   SOL: 9,
//   MATIC: 18,
//   AVAX: 18,
//   FTM: 18,
//   LINK: 18,
// };

// /**
//  * Currency symbols and info
//  */
// export const CURRENCY_SYMBOLS: Record<string, CurrencyInfo> = {
//   USD: {
//     code: 'USD',
//     symbol: '$',
//     name: 'US Dollar',
//     decimals: 2,
//     isFiat: true,
//   },
//   EUR: {
//     code: 'EUR',
//     symbol: '€',
//     name: 'Euro',
//     decimals: 2,
//     isFiat: true,
//   },
//   GBP: {
//     code: 'GBP',
//     symbol: '£',
//     name: 'British Pound',
//     decimals: 2,
//     isFiat: true,
//   },
//   INR: {
//     code: 'INR',
//     symbol: '₹',
//     name: 'Indian Rupee',
//     decimals: 2,
//     isFiat: true,
//   },
//   JPY: {
//     code: 'JPY',
//     symbol: '¥',
//     name: 'Japanese Yen',
//     decimals: 0,
//     isFiat: true,
//   },
//   CNY: {
//     code: 'CNY',
//     symbol: '¥',
//     name: 'Chinese Yuan',
//     decimals: 2,
//     isFiat: true,
//   },
// };

/**
 * Default currency (fallback)
 */
// export const DEFAULT_CURRENCY = CURRENCY_SYMBOLS.USD;

/**
 * Compact notation thresholds
 * Numbers above these values get compact notation
 */
export const COMPACT_NOTATION_THRESHOLDS = {
  K: 1_000,
  M: 1_000_000,
  B: 1_000_000_000,
  T: 1_000_000_000_000,
} as const;

/**
 * Precision tolerance settings
 * Used for comparing decimal numbers safely
 */
export const PRECISION_TOLERANCE = {
  CRYPTO: 1e-8, // BTC satoshi level
  STOCK: 1e-2, // Cent level
  // MEME: 1e-18, // Token-level precision
  BALANCE: 1e-2, // Balance tolerance
} as const;

/**
 * Display limits for very tiny values
 */
export const MINIMUM_DISPLAY_VALUES = {
  CRYPTO: '0.00000001', // 1 Satoshi
  STOCK: '0.01',
  // MEME_COIN: '0.000001',
  USD: '0.01',
} as const;

/**
 * Maximum decimal places to show
 */
export const MAX_DISPLAY_DECIMALS = {
  CRYPTO: 8,
  STOCK: 6,
  // MEME_COIN: 8,
  USD: 2,
  BALANCE: 2,
} as const;

/**
 * Get precision config for a symbol
 */
// export const getPrecisionForSymbol = (symbol: string): AssetType => {
//   if (MEME_COIN_SYMBOLS.has(symbol.toUpperCase())) {
//     return AssetType.MEME_COIN;
//   }
//   if (CRYPTO_SYMBOL_DECIMALS[symbol.toUpperCase()]) {
//     return AssetType.CRYPTO;
//   }
//   return AssetType.STOCK;
// };

/**
 * Get default decimals for a symbol
 */
// export const getDefaultDecimalsForSymbol = (symbol: string): number => {
//   const assetType = getPrecisionForSymbol(symbol);
//   return DEFAULT_ASSET_DECIMALS[assetType];
// };

/**
 * Get currency info
 */
// export const getCurrencyInfo = (code: string): CurrencyInfo => {
//   return CURRENCY_SYMBOLS[code.toUpperCase()] || DEFAULT_CURRENCY;
// };
